import { useEffect, useMemo, useState } from 'react';
import { EventEntity, EventStatus, EventStatusLabel } from 'src/store/eventApi';
import { PageHead } from 'src/ui/patterns/PageHead';
import { SearchInput } from 'src/ui/SearchInput';
import { Button } from 'src/ui/Button';
import { Badge } from 'src/ui/Badge';
import { EmptyState } from 'src/ui/EmptyState';
import { Icon } from 'src/ui/Icon';
import { NEW_EVENT_ID } from './index';

interface OutlineProps {
  events: EventEntity[];
  selectedEventId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
}

const STATUS_BADGE_COLOR: Record<EventStatus, string> = {
  [EventStatus.DRAFT]: 'gray',
  [EventStatus.PUBLISHED]: 'green',
  [EventStatus.CANCELLED]: 'red',
  [EventStatus.COMPLETED]: 'blue',
};

const formatStartAt = (iso: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const Outline = ({
  events,
  selectedEventId,
  onSelect,
  onNew,
}: OutlineProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedTerm(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const filtered = useMemo(() => {
    const term = debouncedTerm.trim().toLowerCase();
    if (!term) return events;
    return events.filter(
      (e) =>
        (e.title || '').toLowerCase().includes(term) ||
        (e.location || '').toLowerCase().includes(term) ||
        (e.description || '').toLowerCase().includes(term)
    );
  }, [events, debouncedTerm]);

  return (
    <div className="col" style={{ height: '100%', overflow: 'hidden' }}>
      <PageHead
        title="Events"
        count={events.length}
        right={
          <Button variant="primary" size="sm" leftIcon="plus" onClick={onNew}>
            New Event
          </Button>
        }
      />

      <div
        style={{
          padding: '10px 16px',
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
        }}
      >
        <SearchInput
          value={searchTerm}
          onChange={setSearchTerm}
          debounce={300}
          placeholder="Search title, location, description…"
          size="sm"
        />
      </div>

      <div className="grow scroll-y" style={{ padding: 6 }}>
        {selectedEventId === NEW_EVENT_ID && (
          <div
            className="list-row selected"
            style={{ fontStyle: 'italic', color: 'var(--text-muted)' }}
          >
            <Icon name="calendar" size={15} />
            New Event (unsaved)
          </div>
        )}

        {filtered.length === 0 && debouncedTerm ? (
          <EmptyState icon="search" title="No events found" />
        ) : filtered.length === 0 &&
          !debouncedTerm &&
          selectedEventId !== NEW_EVENT_ID ? (
          <EmptyState
            icon="calendar"
            title="No events yet"
            desc='Click "New Event" to create one.'
          />
        ) : (
          filtered.map((ev) => (
            <EventListItem
              key={ev.id}
              event={ev}
              selected={selectedEventId === ev.id}
              onClick={() => onSelect(ev.id)}
            />
          ))
        )}
      </div>
    </div>
  );
};

interface EventListItemProps {
  event: EventEntity;
  selected: boolean;
  onClick: () => void;
}

const EventListItem = ({
  event: ev,
  selected,
  onClick,
}: EventListItemProps) => {
  const d = ev.startAt ? new Date(ev.startAt) : null;
  const month = d ? d.toLocaleDateString('en-US', { month: 'short' }) : '';
  const day = d ? d.getDate() : '';

  return (
    <div
      role="button"
      tabIndex={0}
      className={'list-row' + (selected ? ' selected' : '')}
      onClick={onClick}
      style={{ alignItems: 'flex-start' }}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onClick()}
    >
      {d && (
        <div
          style={{
            width: 40,
            textAlign: 'center',
            flexShrink: 0,
            padding: '2px 0',
          }}
        >
          <div
            className="eyebrow"
            style={{
              color: 'var(--accent-text)',
              fontSize: 'var(--fs-2xs)',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            {month}
          </div>
          <div
            style={{
              fontSize: 'var(--fs-xl)',
              fontWeight: 700,
              lineHeight: 1,
              letterSpacing: '-0.02em',
            }}
          >
            {day}
          </div>
        </div>
      )}

      <div className="grow" style={{ minWidth: 0 }}>
        <div className="row gap-2">
          <span
            style={{
              fontWeight: 500,
              fontSize: 'var(--fs-sm)',
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {ev.title || '(untitled)'}
          </span>
          <Badge color={STATUS_BADGE_COLOR[ev.status]} size="sm" dot>
            {EventStatusLabel[ev.status]}
          </Badge>
        </div>
        {ev.location && (
          <div
            className="row gap-1 text-muted"
            style={{ fontSize: 'var(--fs-xs)', marginTop: 2 }}
          >
            <Icon name="map-pin" size={11} />
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {ev.location}
            </span>
          </div>
        )}
        {ev.startAt && (
          <div
            className="row gap-1 text-muted"
            style={{ fontSize: 'var(--fs-xs)', marginTop: 1 }}
          >
            <Icon name="clock" size={11} />
            {formatStartAt(ev.startAt)}
          </div>
        )}
      </div>
    </div>
  );
};

export default Outline;
